import type { Decompte, Etat, Inventaire } from '../partage/api/types';

/**
 * Le décompte de l'inventaire, sans rendu (chantier nettoyage-regles, ticket 01) : ce que les
 * gestes choisis sur les bulles restées changent au « N sur M » du dossier, et les segments de
 * sa barre. Le serveur donne où en est le dossier après la coche, et ce que chaque geste lui
 * changerait ; l'écran n'additionne que ce que tu as choisi. Testé dans
 * `tests/client/inventaire/decompteDeLInventaire.test.ts`.
 */

/**
 * Ce qu'on a décidé d'une bulle restée. Aucune issue, c'est la garder : ne rien faire vaut
 * garder, et « garder » n'est plus un bouton (ticket 28 de finition-v1, décision d'Igor).
 *
 *   · **cochée**   : elle est passée en acquis. `etatAvant` est `null` tant que le serveur
 *                    n'a pas répondu ; c'est lui que le second clic remettra.
 *   · **idée**      : gardée comme idée, « En réflexion » (`sans_objet`) : elle seule sort du
 *                     dénominateur, et redeviendra une tâche dès qu'on lui donnera un critère ;
 *   · **supprimée** : elle part avec tout ce qu'elle contient, après la confirmation du
 *                     ticket 10 (ticket 18 de retours-testeurs : « Jeter » disparaît) ;
 *   · **déplacée**  : elle quitte le dossier (`sortie`) ou change seulement de place dedans,
 *                     et alors le décompte du dossier ne bouge pas.
 */
export type Issue =
  | { sorte: 'cochee'; etatAvant: Etat | null }
  /** Décochée, le serveur n'a pas encore répondu : la recocher croiserait les deux écritures. */
  | { sorte: 'decochage' }
  | { sorte: 'idee' }
  | { sorte: 'supprimee' }
  | { sorte: 'deplacee'; ou: 'dedans' | 'sortie' };

/**
 * Le décompte du dossier une fois les gestes choisis faits : on ajoute, pour chaque bulle
 * cochée, et on retire, pour chaque bulle gardée comme idée, supprimée ou sortie du dossier,
 * ce que le serveur a dit qu'elle pèse. Les bulles restées sont des sœurs, leurs poids ne se chevauchent pas.
 */
export function decompteApres(dossier: Inventaire['dossier'], issues: Map<string, Issue>): Decompte {
  let { faites, total } = dossier;
  for (const [id, issue] of issues) {
    if (issue.sorte === 'cochee') {
      const gain = dossier.ajoute[id];
      if (!gain) continue;
      faites += gain.faites;
      total += gain.total;
      continue;
    }
    if (issue.sorte === 'decochage') continue;
    const poids = dossier.retire[id];
    if (!poids) continue;
    const retrait =
      issue.sorte === 'idee'
        ? poids.siIdee
        : issue.sorte === 'supprimee' || issue.ou === 'sortie'
          ? poids.siSortie
          : null;
    if (!retrait) continue;
    faites -= retrait.faites;
    total -= retrait.total;
  }
  return { faites, total };
}

/** Un segment par bulle du dossier : l'acquis, ce qui reste, et ce qui s'en va — en pointillé. */
export function segments(apres: Decompte, partantes: number): ('acquis' | 'restant' | 'partant')[] {
  return [
    ...Array.from({ length: Math.max(apres.faites, 0) }, () => 'acquis' as const),
    ...Array.from({ length: Math.max(apres.total - apres.faites, 0) }, () => 'restant' as const),
    ...Array.from({ length: Math.max(partantes, 0) }, () => 'partant' as const),
  ];
}
